import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PayrollAdjustmentKind, PayrollRow } from '@aeris/contracts'
import { amountToString, parseAmount } from '@aeris/finance'
import { formatMoney, isPositive } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
  RadioGroup,
} from '@aeris/ui'

import { periodLabel } from '../lib/api/commissions.ts'
import { recordPayrollAdjustment } from '../lib/api/expenses.ts'
import { ApiError } from '../lib/api.ts'

/**
 * Um bônus ou um desconto de UM mês só — F157.
 *
 * *"bônus pontual mensal"*. O bônus fixo mora no cadastro e volta todo mês;
 * este é o da meta batida, o do Natal — e, do outro lado, o do dia que não foi
 * trabalhado.
 *
 * ## Por que não é a tela de gasto
 *
 * **Aqui não sai dinheiro.** Um desconto por falta não tira nada da gaveta: a
 * loja simplesmente deve menos. Lançá-lo como gasto encheria o "Gastos do mês"
 * e o resumo por categoria com dinheiro que nunca existiu.
 *
 * Um bônus também não sai daqui — ele muda o que a loja **deve**. O dinheiro
 * sai depois, no "Pagar", que é lançamento de verdade.
 *
 * ## O sinal é do tipo, nunca do número
 *
 * O valor é sempre positivo e quem decide somar ou subtrair é a escolha em
 * cima. Um campo que aceitasse `-500` inverteria a folha em silêncio no dia em
 * que alguém digitasse o traço sem querer.
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const OPTIONS = [
  {
    value: 'bonus',
    label: 'Bônus',
    description: 'A loja passa a dever mais: meta batida, Natal, um extra.',
  },
  {
    value: 'discount',
    label: 'Desconto',
    description: 'A loja passa a dever menos: uma falta, um atraso.',
  },
]

export const PayrollAdjustmentDialog = ({
  row,
  period,
  onClose,
}: {
  row: PayrollRow
  period: string
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<PayrollAdjustmentKind>('bonus')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  const typed = amount === '' ? null : parseAmount(amount)
  const due = row.dueAmount === null ? null : parseAmount(row.dueAmount)

  const blocker =
    typed === null || !isPositive(typed)
      ? 'Informe o valor.'
      : description.trim() === ''
        ? 'Diga o que é.'
        : /*
           * O mesmo limite do servidor, dito antes de enviar. Não substitui a
           * recusa de lá — o número aqui é o que a tela leu, e pode ter mudado
           * —, mas evita mandar o que já se sabe que não entra.
           */
          kind === 'discount' && (due === null || typed.units > due.units)
          ? `${row.name} tem ${row.dueAmount === null ? 'nada' : money(row.dueAmount)} a receber neste mês; o desconto não pode passar disso.`
          : null

  const save = useMutation({
    mutationFn: async () => {
      await recordPayrollAdjustment({
        employeeId: row.employeeId,
        kind,
        period,
        description: description.trim(),
        amount: amountToString(parseAmount(amount)),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível lançar.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Bônus ou desconto de ${row.name}`}
        description={`Vale só em ${periodLabel(period)}. Não tira dinheiro do caixa: muda o que a loja deve.`}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (blocker === null) {
              setError(null)
              save.mutate()
            }
          }}
        >
          <Field label="O que é">
            {(field) => (
              <RadioGroup
                id={field.id}
                name="payroll-adjustment-kind"
                orientation="horizontal"
                value={kind}
                onValueChange={(value) => {
                  setKind(value as PayrollAdjustmentKind)
                }}
                options={OPTIONS}
              />
            )}
          </Field>

          <Field
            label="Descrição"
            hint="Sai na folha. Em janeiro, é isto que explica o valor."
            required
          >
            {(field) => (
              <Input
                {...field}
                value={description}
                maxLength={200}
                placeholder={
                  kind === 'bonus' ? 'Meta de setembro' : 'Faltou dia 12'
                }
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
              />
            )}
          </Field>

          <Field label="Valor" required>
            {(field) => (
              <MoneyInput {...field} value={amount} onValueChange={setAmount} />
            )}
          </Field>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter className="items-center justify-between">
            <span className="text-(length:--text-sm) text-(--text-muted)">
              {blocker ??
                (row.dueAmount === null
                  ? ''
                  : `Hoje: ${money(row.dueAmount)} a receber.`)}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                aria-disabled={blocker !== null}
                className={blocker === null ? undefined : 'opacity-60'}
                loading={save.isPending}
              >
                Lançar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
